import { useEffect, useMemo, useRef, useState } from 'react'
import { services, themes } from './themes'

function ArrowIcon({ direction = 'right' }) {
  const rotation = direction === 'down' ? 90 : direction === 'up' ? -90 : direction === 'left' ? 180 : 0
  return (
    <svg className="icon" viewBox="0 0 24 24" aria-hidden="true" style={{ transform: `rotate(${rotation}deg)` }}>
      <path d="M5 12h13M13 6l6 6-6 6" />
    </svg>
  )
}

function ServiceIcon({ type }) {
  if (type === 'mail') {
    return <svg viewBox="0 0 48 48" aria-hidden="true"><rect x="5" y="9" width="38" height="30" rx="3"/><path d="m7 12 17 14 17-14"/></svg>
  }
  if (type === 'send') {
    return <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M43 6 5 22l15 6 6 14L43 6Z"/><path d="m20 28 10-9"/></svg>
  }
  return <svg viewBox="0 0 48 48" aria-hidden="true"><rect x="5" y="7" width="38" height="34" rx="3"/><path d="M5 15h38M11 11h.1M16 11h.1M21 11h.1"/></svg>
}

function PortalMark() {
  return (
    <span className="portal-mark" aria-hidden="true">
      <span />
    </span>
  )
}

function Header({ onBrief }) {
  return (
    <header className="site-header">
      <a className="brand" href="#top" aria-label="В начало">
        <PortalMark />
        <span>CREATIVE<br />DEVELOPER</span>
      </a>
      <nav aria-label="Главная навигация">
        <a href="#styles">Стили</a>
        <a href="#services">Услуги</a>
        <a href="#process">Процесс</a>
      </nav>
      <button className="header-cta" type="button" onClick={onBrief}>
        Обсудить проект <ArrowIcon />
      </button>
    </header>
  )
}

function StylePicker({ activeId, onSelect }) {
  const otherThemes = themes.filter((theme) => theme.id !== activeId)
  return (
    <div className="style-picker" aria-label="Другие стили портфолио">
      {otherThemes.map((theme) => {
        return (
          <button
            className="style-tile"
            type="button"
            key={theme.id}
            onClick={() => onSelect(theme.id)}
            aria-label={`${theme.number}. ${theme.title}`}
          >
            <img src={theme.image} alt="" loading={theme.number === '01' ? 'eager' : 'lazy'} />
            <span className="style-tile-shade" />
            <span className="style-number">{theme.number}</span>
            <span className="style-name">{theme.title}</span>
            <span className="style-dots"><i /><i /><i /></span>
          </button>
        )
      })}
    </div>
  )
}

function Hero({ activeTheme, onSelect, onBrief }) {
  return (
    <section className="hero" id="top" aria-labelledby="hero-title">
      <Header onBrief={onBrief} />
      <div className="hero-main">
        <div className="hero-copy">
          <h1 id="hero-title">В каком стиле<br />показать мою работу?</h1>
          <p>Сайты <span>·</span> HTML-письма <span>·</span> Telegram-боты</p>
          <div className="hero-actions">
            <a className="primary-action" href="#styles">Выбрать стиль <ArrowIcon direction="down" /></a>
            <a className="text-action" href="#services">Смотреть всё сразу <ArrowIcon /></a>
          </div>
        </div>

        <div className="live-preview" aria-live="polite" aria-label={`Выбранный стиль: ${activeTheme.title}`}>
          <img key={activeTheme.id} src={activeTheme.image} alt={`Визуальный стиль ${activeTheme.title}`} />
          <div className="preview-vignette" />
          <div className="preview-topline">
            <span>{activeTheme.number}</span>
            <span>LIVE STYLE</span>
          </div>
          <div className="preview-caption">
            <span>{activeTheme.short}</span>
            <h2>{activeTheme.title}</h2>
            <a href="#styles">Исследовать <ArrowIcon /></a>
          </div>
          <div className="preview-count">{activeTheme.number} <i /> 10</div>
        </div>
      </div>

      <StylePicker activeId={activeTheme.id} onSelect={onSelect} />
      <a className="scroll-cue" href="#styles" aria-label="Прокрутить к выбранному стилю">
        <span>ПРОКРУТИТЕ</span>
        <ArrowIcon direction="down" />
      </a>
    </section>
  )
}

function ActiveWorld({ theme, onNext }) {
  return (
    <section className="active-world reveal" id="styles" aria-labelledby="world-title">
      <div className="section-index">{theme.number} / 10</div>
      <div className="world-copy">
        <p className="section-label">Выбранный мир</p>
        <h2 id="world-title">{theme.title}</h2>
        <p className="world-lead">{theme.description}</p>
        <dl>
          <div><dt>Движение</dt><dd>{theme.motion}</dd></div>
          <div><dt>Подходит для</dt><dd>{theme.bestFor}</dd></div>
        </dl>
        <button className="next-theme" type="button" onClick={onNext}>
          Следующий стиль <ArrowIcon />
        </button>
      </div>
      <div className="world-visual">
        <div className="world-orbit" aria-hidden="true" />
        <img key={theme.id} src={theme.image} alt={`Полноэкранный концепт ${theme.title}`} />
        <span className="world-note">Смена стиля меняет весь интерфейс</span>
      </div>
    </section>
  )
}

function Services() {
  return (
    <section className="services-section" id="services" aria-labelledby="services-title">
      <div className="section-heading reveal">
        <p className="section-label">Что я создаю</p>
        <h2 id="services-title">От идеи до<br />работающего продукта</h2>
        <p>Дизайн, разработка и запуск собраны в одном процессе. Стиль выбирается под задачу, а не наоборот.</p>
      </div>
      <div className="service-list">
        {services.map((service) => (
          <article className="service-row reveal" key={service.number}>
            <span className="service-number">{service.number}</span>
            <span className="service-icon"><ServiceIcon type={service.icon} /></span>
            <div className="service-main">
              <h3>{service.title}</h3>
              <p>{service.text}</p>
            </div>
            <ul>{service.details.map((item) => <li key={item}>{item}</li>)}</ul>
          </article>
        ))}
      </div>
    </section>
  )
}

function ThemeCatalog({ activeId, onSelect }) {
  return (
    <section className="catalog-section" aria-labelledby="catalog-title">
      <div className="catalog-heading reveal">
        <p className="section-label">10 визуальных систем</p>
        <h2 id="catalog-title">Одна задача.<br />Десять характеров.</h2>
      </div>
      <div className="catalog-grid">
        {themes.map((theme) => (
          <button
            type="button"
            className={`catalog-card reveal${theme.id === activeId ? ' is-active' : ''}`}
            key={theme.id}
            onClick={() => {
              onSelect(theme.id)
              document.querySelector('#styles')?.scrollIntoView({ behavior: 'smooth' })
            }}
          >
            <span className="catalog-image"><img src={theme.image} alt="" loading="lazy" /></span>
            <span className="catalog-meta"><i>{theme.number}</i><strong>{theme.title}</strong><small>{theme.short}</small></span>
          </button>
        ))}
      </div>
    </section>
  )
}

function Process() {
  const steps = [
    ['01', 'Задача', 'Разбираю продукт, аудиторию и цель. Определяем, что должен сделать посетитель.'],
    ['02', 'Направление', 'Выбираем характер, сетку, типографику и механику движения.'],
    ['03', 'Разработка', 'Собираю адаптивный интерфейс и проверяю основные сценарии.'],
    ['04', 'Запуск', 'Оптимизирую, подключаю аналитику и публикую готовый проект.'],
  ]
  return (
    <section className="process-section" id="process" aria-labelledby="process-title">
      <div className="process-title reveal">
        <p className="section-label">Как строится проект</p>
        <h2 id="process-title">Четыре шага<br />до запуска</h2>
      </div>
      <ol className="process-track">
        {steps.map(([number, title, text]) => (
          <li className="reveal" key={number}>
            <span>{number}</span>
            <h3>{title}</h3>
            <p>{text}</p>
          </li>
        ))}
      </ol>
    </section>
  )
}

function ContactSection({ onBrief }) {
  return (
    <section className="contact-section reveal" aria-labelledby="contact-title">
      <div>
        <p className="section-label">Следующий проект</p>
        <h2 id="contact-title">Давайте создадим<br />ваш цифровой мир.</h2>
      </div>
      <button type="button" className="contact-orbit" onClick={onBrief}>
        <span>Сформировать бриф</span>
        <ArrowIcon />
      </button>
      <footer>
        <span>Сайты · HTML-письма · Telegram-боты</span>
        <a href="#top">Наверх <ArrowIcon direction="up" /></a>
      </footer>
    </section>
  )
}

function BriefDialog({ open, onClose, activeTheme }) {
  const dialogRef = useRef(null)
  const [copied, setCopied] = useState(false)
  const [form, setForm] = useState({ name: '', type: 'Сайт', details: '' })

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  const briefText = `Здравствуйте! Меня зовут ${form.name || '...'}. Хочу обсудить проект: ${form.type}. Выбранный стиль: ${activeTheme.title}. Задача: ${form.details || '...'}`

  const copyBrief = async () => {
    await navigator.clipboard.writeText(briefText)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2400)
  }

  const shareBrief = async () => {
    if (navigator.share) await navigator.share({ title: 'Бриф на новый проект', text: briefText })
    else await copyBrief()
  }

  return (
    <dialog ref={dialogRef} className="brief-dialog" onClose={onClose} onClick={(event) => event.target === dialogRef.current && onClose()}>
      <form method="dialog" onSubmit={(event) => event.preventDefault()}>
        <button className="dialog-close" type="button" onClick={onClose} aria-label="Закрыть">×</button>
        <p className="section-label">Короткий бриф</p>
        <h2>Расскажите о задаче</h2>
        <label>Как вас зовут<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ваше имя" /></label>
        <label>Что нужно создать<select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}><option>Сайт</option><option>HTML-письмо</option><option>Telegram-бот</option><option>Комплексный проект</option></select></label>
        <label>О проекте<textarea value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="Цель, аудитория и важные детали" rows="4" /></label>
        <div className="brief-preview">{briefText}</div>
        <div className="dialog-actions">
          <button type="button" onClick={copyBrief}>{copied ? 'Бриф скопирован' : 'Скопировать бриф'}</button>
          <button type="button" className="share-button" onClick={shareBrief}>Поделиться <ArrowIcon /></button>
        </div>
      </form>
    </dialog>
  )
}

export default function App() {
  const initialId = useMemo(() => {
    const query = new URLSearchParams(window.location.search).get('style')
    return themes.some((theme) => theme.id === query) ? query : 'aurora-glass'
  }, [])
  const [activeId, setActiveId] = useState(initialId)
  const [briefOpen, setBriefOpen] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const activeTheme = themes.find((theme) => theme.id === activeId) ?? themes[2]

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => entry.target.classList.toggle('is-visible', entry.isIntersecting))
    }, { threshold: 0.12 })
    document.querySelectorAll('.reveal').forEach((node) => observer.observe(node))
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight
      setScrollProgress(max > 0 ? window.scrollY / max : 0)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const url = new URL(window.location.href)
    url.searchParams.set('style', activeId)
    window.history.replaceState({}, '', url)
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', activeTheme.tokens.bg)
  }, [activeId, activeTheme.tokens.bg])

  const selectTheme = (id) => setActiveId(id)
  const nextTheme = () => {
    const index = themes.findIndex((theme) => theme.id === activeId)
    setActiveId(themes[(index + 1) % themes.length].id)
  }

  const themeStyle = Object.fromEntries(Object.entries(activeTheme.tokens).map(([key, value]) => [`--${key}`, value]))

  return (
    <div className="portfolio-app" data-theme={activeTheme.id} style={themeStyle}>
      <div className="page-progress" style={{ transform: `scaleX(${scrollProgress})` }} />
      <div className="ambient ambient-a" aria-hidden="true" />
      <div className="ambient ambient-b" aria-hidden="true" />
      <Hero activeTheme={activeTheme} onSelect={selectTheme} onBrief={() => setBriefOpen(true)} />
      <main>
        <ActiveWorld theme={activeTheme} onNext={nextTheme} />
        <Services />
        <ThemeCatalog activeId={activeId} onSelect={selectTheme} />
        <Process />
        <ContactSection onBrief={() => setBriefOpen(true)} />
      </main>
      <BriefDialog open={briefOpen} onClose={() => setBriefOpen(false)} activeTheme={activeTheme} />
    </div>
  )
}
